// library/SmartFilterBar — the saved searches (Smart Filters) as one quiet
// row under the query field: one chip per saved search (a click applies its
// query and sort), the focused chip's small menu (rename, update to the
// search shown, delete), "Save search" while the search shown is not
// saved yet, and "Search removed · Undo" right after a delete (until the
// next action: no toast, no timer). Nothing renders until there is something to show (progressive
// disclosure: no saved search and no query, no row).
//
// A projection + a command mapping: the chips come from
// `projectSmartFilters`; every intent is a module Msg. Local state is
// view-ephemeral only: which name field is open (an un-submitted name).

import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import { type ExternalError, toDisplay } from "../../app/externalError.js";
import { Button } from "../../components/Button/Button.js";
import type { MenuItem } from "../../components/Menu/Menu.js";
import { RowOverflow } from "../../components/RowOverflow/RowOverflow.js";
import { RowRename } from "../../components/RowOverflow/RowRename.js";
import { Text } from "../../components/Text/Text.js";
import type { LibraryMsg } from "./msg.js";
import type { SmartFiltersProjection } from "./view-model.js";

// The name field that is open: a new search's, or one saved search's.
type Naming =
    | { readonly type: "none" }
    | { readonly type: "new" }
    | { readonly type: "rename"; readonly id: string };

export const SmartFilterBar = ({
    view,
    query,
    failure,
    send,
}: {
    readonly view: SmartFiltersProjection;
    readonly query: string;
    readonly failure: ExternalError | null;
    readonly send: (msg: LibraryMsg) => void;
}): ReactNode => {
    const { t } = useLingui();
    const [naming, setNaming] = useState<Naming>({ type: "none" });
    const close = (): void => setNaming({ type: "none" });

    const failureText = (e: ExternalError): string => {
        const d = toDisplay(e);
        return d.body === undefined ? t(d.title) : `${t(d.title)} ${t(d.body)}`;
    };

    if (
        view.chips.length === 0 &&
        !view.canSave &&
        naming.type === "none" &&
        failure === null &&
        !view.canUndoDelete
    )
        return null;

    const menuFor = (id: string): readonly MenuItem[] => {
        const update: readonly MenuItem[] = view.canUpdate
            ? [
                  {
                      _tag: "item",
                      label: t`Update to the current search`,
                      onSelect: () =>
                          send({ type: "smart_filter_query_updated", id }),
                  },
              ]
            : [];
        return [
            {
                _tag: "item",
                label: t`Rename`,
                onSelect: () => setNaming({ type: "rename", id }),
            },
            ...update,
            {
                _tag: "item",
                label: t`Delete`,
                tone: "danger",
                onSelect: () => send({ type: "smart_filter_deleted", id }),
            },
        ];
    };

    return (
        <div className="bsx-saved">
            <ul className="bsx-saved-list" aria-label={t`Saved searches`}>
                {view.chips.map(({ filter, current }) => {
                    const { id, name } = filter;
                    // Named `title` to share the rows' messages.
                    const title = name;
                    if (naming.type === "rename" && naming.id === id)
                        return (
                            <li key={id} className="bsx-saved-item">
                                <RowRename
                                    initial={name}
                                    label={t`New name for ${title}`}
                                    onSubmit={(next) => {
                                        close();
                                        send({
                                            type: "smart_filter_renamed",
                                            id,
                                            name: next,
                                        });
                                    }}
                                    onCancel={close}
                                />
                            </li>
                        );
                    return (
                        <li key={id} className="bsx-saved-item">
                            <button
                                type="button"
                                aria-pressed={current}
                                className={`bsx-saved-chip${current ? " bsx-saved-chip--current" : ""}`}
                                onClick={() =>
                                    send({
                                        type: "smart_filter_applied",
                                        filter,
                                    })
                                }
                            >
                                <Text
                                    as="span"
                                    size="sm"
                                    weight={current ? "semibold" : "medium"}
                                    className="bsx-saved-label"
                                >
                                    {name}
                                </Text>
                            </button>
                            {view.focused?.id === id ? (
                                <RowOverflow
                                    label={t`Actions for ${title}`}
                                    items={menuFor(id)}
                                />
                            ) : null}
                        </li>
                    );
                })}
                {naming.type === "new" ? (
                    <li className="bsx-saved-item">
                        <RowRename
                            initial={query.trim()}
                            suggested
                            label={t`Name for the saved search`}
                            onSubmit={(name) => {
                                close();
                                send({ type: "smart_filter_saved", name });
                            }}
                            onCancel={close}
                        />
                    </li>
                ) : view.canSave ? (
                    <li className="bsx-saved-item">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setNaming({ type: "new" })}
                        >
                            {t`Save search`}
                        </Button>
                    </li>
                ) : null}
            </ul>
            {/* A status is announced as it appears. */}
            {view.canUndoDelete ? (
                <div role="status" className="bsx-saved-undo">
                    <Text as="span" size="xs" tone="dim">
                        {t`Search removed`}
                    </Text>
                    <span aria-hidden="true">·</span>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => send({ type: "smart_filter_restored" })}
                    >
                        {t`Undo`}
                    </Button>
                </div>
            ) : null}
            {/* An alert is announced as it appears. */}
            {failure !== null ? (
                <div role="alert">
                    <Text as="p" size="xs" tone="dim">
                        {failureText(failure)}
                    </Text>
                </div>
            ) : null}
        </div>
    );
};
